import {
  Component,
  effect,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  untracked,
} from "@angular/core";

import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { CollectionService } from "@bitwarden/common/abstractions/collection.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { OrganizationUserService } from "@bitwarden/common/abstractions/organization-user/organization-user.service";
import { OrganizationService } from "@bitwarden/common/abstractions/organization/organization.service.abstraction";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { DaliService } from "@bitwarden/web-vault/app/services/dali/dali.service";

import { buildMembersLabel, loadMembersByCollection } from "../team-members.utils";

interface SharedTeamRow {
  collectionId: string;
  name: string;
  initial: string;
  membersLabel: string;
}

/**
 * "Sharing" summary card shown on the account detail pane. Lists the teams this account is
 * already shared into (read-only — there's no "frozen"/paused share state or per-team edit view
 * in this app yet, so every row is a live, fully-shared team); the actual team picker for adding
 * new shares lives in the ShareAccountComponent slide-out, opened via `shareWithTeam`.
 */
@Component({
  selector: "app-account-linked-collection",
  templateUrl: "./account-linked-collection.component.html",
})
export class AccountLinkedCollectionComponent implements OnChanges {
  @Input() account: AccountView | null = null;
  @Output() shareWithTeam = new EventEmitter<void>();

  private apiService = inject(ApiService);
  private collectionService = inject(CollectionService);
  private daliService = inject(DaliService);
  private i18nService = inject(I18nService);
  private logService = inject(LogService);
  private organizationService = inject(OrganizationService);
  private organizationUserService = inject(OrganizationUserService);

  protected rows: SharedTeamRow[] = [];

  // load() is async and can be triggered again (account switch, share completing) before the
  // previous run finishes — only the latest run is allowed to write `rows`.
  private loadSeq = 0;
  private loadedVaultId: string | undefined;

  constructor() {
    // Sharing an already org-owned account into another collection doesn't touch the vault
    // store `account` reads from (see DaliService.sharedVersion), so `account` alone won't
    // re-trigger ngOnChanges after a share completes — this effect is the reload path for that.
    // ngOnChanges already covers the initial load, so only react to versions after this one.
    let lastVersion = untracked(() => this.daliService.sharedVersion());
    effect(() => {
      const version = this.daliService.sharedVersion();
      if (version === lastVersion) {
        return;
      }
      lastVersion = version;
      // load() runs synchronously up to its first await — keep that part out of the effect's
      // dependency tracking.
      untracked(() => void this.load());
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["account"]) {
      // Switching to a different account must not keep showing the previous account's teams
      // while the new load is in flight. A re-emit of the same account keeps its rows.
      const vaultId = this.account?.getVaultId();
      if (vaultId !== this.loadedVaultId) {
        this.rows = [];
      }
      this.loadedVaultId = vaultId;
      void this.load();
    }
  }

  private async load() {
    const seq = ++this.loadSeq;
    const vaultId = this.account?.getVaultId();
    if (!vaultId) {
      this.rows = [];
      return;
    }

    try {
      const collectionIds = await this.daliService.getCollectionIds(vaultId);
      if (seq !== this.loadSeq) {
        return;
      }
      if (collectionIds.length === 0) {
        this.rows = [];
        return;
      }

      const allCollections = await this.collectionService.getAllDecrypted();
      const sharedCollections = allCollections.filter((c) => collectionIds.includes(c.id));
      const membersByCollection = await loadMembersByCollection(
        this.organizationUserService,
        this.apiService,
        this.organizationService,
        sharedCollections,
        this.logService,
      );
      if (seq !== this.loadSeq) {
        return;
      }

      this.rows = sharedCollections.map((c) => ({
        collectionId: c.id,
        name: c.name,
        initial: c.name?.charAt(0).toUpperCase() ?? "",
        membersLabel: buildMembersLabel(this.i18nService, membersByCollection.get(c.id) ?? []),
      }));
    } catch (e) {
      if (seq !== this.loadSeq) {
        return;
      }
      this.logService.error(e instanceof Error ? (e.stack ?? e.message) : JSON.stringify(e));
      this.rows = [];
    }
  }
}
